import type { DashboardRange } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Skeleton, StatCard } from "@sagot/ui";
import type { DashboardOut } from "../shared/api-types";
import { deltaOf, seconds, signedPercent, signedPoints, toPercent } from "./dashboard-model";
import { staffDurationText } from "./kpi-durations";

type Kpis = DashboardOut["kpis"];

const GRID = "grid grid-cols-2 gap-4 md:grid-cols-4";
const TILES = 7;

export function KpiRowSkeleton() {
  return (
    <section className={GRID} aria-hidden="true">
      {Array.from({ length: TILES }, (_, i) => (
        <div key={i} className="card card-body space-y-2">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-8 w-16" />
          <Skeleton className="h-3 w-28" />
        </div>
      ))}
    </section>
  );
}

function useDelta(range: DashboardRange) {
  const { t } = useTranslation();
  const suffix = t(`dashboard.vsPrevious.${range}`);
  return (pct: number | null, higherIsBetter = true) => deltaOf(pct, `${pct === null ? "" : signedPercent(pct)} ${suffix}`, higherIsBetter);
}

/** Seven KPI tiles (the assistant's answer time and the staff first response are separate measures, RPT-004); every value has a text delta or footnote (never colour alone). */
export function KpiRow({ kpis, range, formatNumber }: { kpis: Kpis; range: DashboardRange; formatNumber: (n: number) => string }) {
  const { t } = useTranslation();
  const delta = useDelta(range);
  const dash = "—";
  const resolution = kpis.ai_resolution_rate === null ? dash : `${String(Math.round(toPercent(kpis.ai_resolution_rate)))}%`;
  const answerTime = kpis.ai_answer_time_avg_ms === null ? dash : t("dashboard.kpi.seconds", { value: seconds(kpis.ai_answer_time_avg_ms) });
  const answerP95 = kpis.ai_answer_time_p95_ms === null ? null : t("agentReports.kpi.p95", { value: t("dashboard.kpi.seconds", { value: seconds(kpis.ai_answer_time_p95_ms) }) });
  const staffTime = kpis.staff_first_response_avg_ms === null ? dash : staffDurationText(kpis.staff_first_response_avg_ms, t);
  const staffP95 = kpis.staff_first_response_p95_ms === null ? null : t("agentReports.kpi.p95", { value: staffDurationText(kpis.staff_first_response_p95_ms, t) });
  const pts = kpis.ai_resolution_delta_pts;
  return (
    <section className={GRID} aria-label={t("dashboard.title")}>
      <StatCard label={t("dashboard.kpi.conversations")} icon="messages-square" value={formatNumber(kpis.conversations)} delta={delta(kpis.conversations_delta_pct)} footnote={kpis.conversations_delta_pct === null ? t("dashboard.kpi.noComparison") : undefined} />
      <StatCard
        label={t("dashboard.kpi.resolution")}
        icon="check-circle"
        value={resolution}
        delta={pts === null ? undefined : deltaOf(pts, t("dashboard.kpi.pts", { value: signedPoints(pts) }))}
        footnote={pts === null ? t("dashboard.kpi.noComparison") : undefined}
      />
      <StatCard label={t("agentReports.kpi.aiAnswerTime")} icon="zap" value={answerTime} footnote={[answerP95, t("agentReports.kpi.aiAnswerNote")].filter(Boolean).join(" · ")} />
      <StatCard
        label={t("agentReports.kpi.staffFirstResponse")}
        icon="timer"
        value={staffTime}
        footnote={kpis.staff_first_response_avg_ms === null ? t("agentReports.kpi.staffNone") : [staffP95, t("agentReports.kpi.staffNote")].filter(Boolean).join(" · ")}
      />
      <StatCard label={t("dashboard.kpi.handedToStaff")} icon="headset" value={formatNumber(kpis.handed_to_staff)} delta={delta(kpis.handed_to_staff_delta_pct, false)} footnote={kpis.handed_to_staff_delta_pct === null ? t("dashboard.kpi.noComparison") : undefined} />
      <StatCard label={t("dashboard.kpi.leads")} icon="user-check" value={formatNumber(kpis.leads)} delta={delta(kpis.leads_delta_pct)} footnote={kpis.leads_delta_pct === null ? t("dashboard.kpi.noComparison") : undefined} />
      <StatCard
        label={t("dashboard.kpi.satisfaction")}
        icon="thumbs-up"
        value={
          kpis.satisfaction_avg === null ? (
            dash
          ) : (
            <>
              {kpis.satisfaction_avg.toFixed(1)}
              <span className="text-base font-bold text-fg-subtle">{t("dashboard.kpi.outOf")}</span>
            </>
          )
        }
        footnote={kpis.satisfaction_ratings > 0 ? t("dashboard.kpi.ratings", { count: kpis.satisfaction_ratings }) : t("dashboard.kpi.noRatings")}
      />
    </section>
  );
}
